import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { LineChartOutlined, ShopOutlined } from "@ant-design/icons";

// 登录由后端维护 token，前端不再提供登录页
import StoreRevenueMonitor from "./pages/store-revenue-monitor/index";
import StoreRevenueComponentPage from "./pages/store-revenue-monitor/ComponentPage";
import StoreRevenueComponentPageV2 from "./pages/store-revenue-monitor/ComponentPageV2";
import StoreRevenueSingle from "./pages/store-revenue-single/index";
import ScreenshotViewer from "./pages/store-revenue-monitor/ScreenshotViewer";
import WecomCallbackPage from "./pages/wecom-callback/index";
import PcAuthGuard from "./components/PcAuthGuard";
import { AppTitleLayout } from "./components/AppTitleLayout";
import { LayoutShellProvider } from "./components/LayoutShellContext";

const RootLayout: React.FC = () => (
  <LayoutShellProvider>
    <AppTitleLayout>
      <Outlet />
    </AppTitleLayout>
  </LayoutShellProvider>
);

const cleanRouteConfig = (route: any): any => {
  const cleaned: any = {
    path: route.path,
    element: route.element,
  };

  if (route.children && Array.isArray(route.children)) {
    cleaned.children = route.children.map(cleanRouteConfig);
  }

  if (route.index !== undefined) {
    cleaned.index = route.index;
  }

  if (route.caseSensitive !== undefined) {
    cleaned.caseSensitive = route.caseSensitive;
  }

  return cleaned;
};

export const getRouteObjects = (): any[] => {
  const cleanedRoutes = routes.map(cleanRouteConfig);
  return cleanedRoutes;
};

export interface AppRoute {
  path?: string;
  element?: React.ReactNode;
  index?: boolean;
  children?: AppRoute[];
  caseSensitive?: boolean;
  key?: string;
  label?: string;
  icon?: React.ReactNode;
  hideInMenu?: boolean;
}

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <PcAuthGuard>{children}</PcAuthGuard>
);

/** 未知路径统一回到集团运营（门店营收监控）页 */
const CatchAllRedirect: React.FC = () => (
  <Navigate to="/store-revenue-monitor" replace />
);

export const routes: AppRoute[] = [
  {
    path: "/wecom-callback",
    element: <WecomCallbackPage />,
    key: "wecom.callback",
    hideInMenu: true,
  },
  {
    path: "/store-revenue-monitor/screenshot",
    element: (
      <ProtectedRoute>
        <StoreRevenueMonitor />
      </ProtectedRoute>
    ),
    key: "store.revenue.monitor.screenshot",
    hideInMenu: true,
  },
  {
    path: "/store-revenue-monitor/screenshot-v2",
    element: (
      <ProtectedRoute>
        <StoreRevenueMonitor />
      </ProtectedRoute>
    ),
    key: "store.revenue.monitor.screenshot.v2",
    hideInMenu: true,
  },
  {
    path: "/store-revenue-monitor/screenshot-viewer",
    element: (
      <ProtectedRoute>
        <ScreenshotViewer />
      </ProtectedRoute>
    ),
    key: "store.revenue.monitor.screenshot.viewer",
    hideInMenu: true,
  },
  {
    path: "/store-revenue-monitor/component",
    element: (
      <ProtectedRoute>
        <StoreRevenueComponentPage />
      </ProtectedRoute>
    ),
    key: "store.revenue.monitor.component",
    hideInMenu: true,
  },
  {
    path: "/store-revenue-monitor/component-v2",
    element: (
      <ProtectedRoute>
        <StoreRevenueComponentPageV2 />
      </ProtectedRoute>
    ),
    key: "store.revenue.monitor.component.v2",
    hideInMenu: true,
  },
  {
    path: "/",
    element: <RootLayout />,
    key: "root",
    children: [
      {
        path: "store-revenue-monitor",
        element: (
          <ProtectedRoute>
            <StoreRevenueMonitor />
          </ProtectedRoute>
        ),
        key: "store.revenue.monitor",
        label: "集团运营数据",
        icon: <LineChartOutlined />,
      },
      {
        path: "store-revenue-single",
        element: (
          <ProtectedRoute>
            <StoreRevenueSingle />
          </ProtectedRoute>
        ),
        key: "store.revenue.single",
        label: "运行信息",
        icon: <ShopOutlined />,
        hideInMenu: true,
      },
      {
        path: "",
        element: <Navigate to="/store-revenue-monitor" replace />,
        key: "home",
        hideInMenu: true,
      },
      {
        path: "*",
        element: <CatchAllRedirect />,
        key: "404",
        hideInMenu: true,
      },
    ],
  },
];

export const getMenuItems = () => {
  const rootRoute = routes.find((route) => route.path === "/");
  if (rootRoute && rootRoute.children) {
    return rootRoute.children.filter((route) => !route.hideInMenu);
  }
  return [];
};

export default routes;
